/**
 * Typed fetch wrapper for the FastAPI backend.
 *
 * In dev : Vite proxies /api/* → http://127.0.0.1:8080
 * In prod : SPA is served by FastAPI on the same origin, so relative URLs
 *           just work.
 *
 * Auth : if CEE_DASHBOARD_TOKEN is configured server-side, set the bearer
 * token here (set via VITE_API_TOKEN or read from localStorage key 'lab.apitoken').
 */

import type {
  SystemsResponse,
  RunsResponse,
  Run,
  LaunchRequest,
  LaunchResponse,
  PreflightResponse,
  Suite,
  TCParamsResponse,
  TCParamsOverrides,
  YamlTestSummary,
  UserSuite,
  SentinelSnapshot,
  SentinelRealResponse,
  SentinelAlertsResponse,
  RunDetail,
  JSONSchema,
  HookMeta,
  SchemaError,
} from './types';
// Re-export so consumers can `import { RunDetail } from '@/lib/api'`
export type { RunDetail, JSONSchema, HookMeta, SchemaError };

const BASE = '/api';

class ApiError extends Error {
  constructor(public status: number, public body: unknown, message?: string) {
    super(message ?? `API error ${status}`);
    this.name = 'ApiError';
  }
}

function authHeader(): Record<string, string> {
  const token =
    (typeof import.meta !== 'undefined' &&
      (import.meta as unknown as { env?: Record<string, string> }).env?.VITE_API_TOKEN) ||
    (typeof localStorage !== 'undefined' ? localStorage.getItem('lab.apitoken') : '') ||
    '';
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Default per-call timeout. Any backend endpoint slower than 20s is almost
 * certainly hung — we'd rather surface an error than let React Query keep
 * a spinner forever (CLAUDE.md §-1, enterprise-grade robustness).
 *
 * Callers can override via the second argument (`timeoutMs`) or pass an
 * explicit AbortSignal in `init.signal` (in which case we don't wrap again).
 */
const DEFAULT_TIMEOUT_MS = 20_000;

async function request<T>(
  path: string,
  init: RequestInit = {},
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<T> {
  // Belt-and-suspenders timeout — even if the server hangs, the promise
  // resolves with an ApiError so the calling React Query observes a failure.
  const ctrl = init.signal ? null : new AbortController();
  const timer = ctrl
    ? setTimeout(() => ctrl.abort(new Error(`Timeout ${timeoutMs}ms`)), timeoutMs)
    : null;
  try {
    const res = await fetch(`${BASE}${path}`, {
      ...init,
      signal: init.signal ?? ctrl?.signal,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...authHeader(),
        ...(init.headers ?? {}),
      },
    });
    if (!res.ok) {
      let body: unknown = null;
      try {
        body = await res.json();
      } catch {
        body = await res.text().catch(() => null);
      }
      throw new ApiError(res.status, body);
    }
    if (res.status === 204) return undefined as unknown as T;
    return (await res.json()) as T;
  } catch (exc) {
    // Surface AbortError as an ApiError(408, …) so callers don't have to
    // discriminate between cancel and network in UI code.
    if (exc instanceof Error && exc.name === 'AbortError') {
      throw new ApiError(408, { detail: exc.message || 'Request aborted' }, 'Request aborted / timeout');
    }
    throw exc;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export const api = {
  // ── Health / Systems ──────────────────────────────────────────────────
  health:  () => request<{ status: string }>('/health'),
  systems: () => request<SystemsResponse>('/systems'),

  // ── Runs ──────────────────────────────────────────────────────────────
  listRuns: (params: { status?: string; system?: string; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.system) qs.set('system', params.system);
    if (params.limit != null) qs.set('limit', String(params.limit));
    const tail = qs.toString() ? `?${qs.toString()}` : '';
    return request<RunsResponse>(`/runs${tail}`);
  },
  /** Parsed run (output.xml). Heavy — includes suites and keywords. */
  getRun:      (runId: string) => request<import('./types').RunDetail>(`/runs/${encodeURIComponent(runId)}`),
  /** Light metadata (meta.json). Used for polling status / system / mode. */
  getRunStatus:(runId: string) => request<Run>(`/runs/${encodeURIComponent(runId)}/status`),
  stopRun:     (runId: string, mode: 'safe' | 'hard' = 'safe') =>
    request<{ ok: boolean }>(`/runs/${encodeURIComponent(runId)}/stop`, {
      method: 'POST',
      body: JSON.stringify({ mode }),
    }),
  /** Emergency stop — halt every active run on the platform. */
  stopAllRuns: (mode: 'safe' | 'hard' = 'safe') =>
    request<{ mode: string; count: number; stopped: { run_id: string; ok: boolean }[]; failures: { run_id: string; ok: false; reason: string }[] }>(`/runs/stop-all`, {
      method: 'POST',
      body: JSON.stringify({ mode }),
    }),
  deleteRun:   (runId: string) => request<void>(`/runs/${encodeURIComponent(runId)}`, { method: 'DELETE' }),
  renameRun:   (runId: string, name: string) =>
    request<{ ok: boolean }>(`/runs/${encodeURIComponent(runId)}/name`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),

  // ── Launch ────────────────────────────────────────────────────────────
  launch:    (req: LaunchRequest) => request<LaunchResponse>('/launch', { method: 'POST', body: JSON.stringify(req) }),
  preflight: (req: Partial<LaunchRequest>) => request<PreflightResponse>('/preflight', { method: 'POST', body: JSON.stringify(req) }),
  /** Read the preflight snapshot persisted at launch time (audit evidence). */
  runPreflight: (runId: string) =>
    request<{ preflight: { checks: import('./types').PreflightCheck[]; ok: boolean; captured_at?: string } | null }>(
      `/runs/${encodeURIComponent(runId)}/preflight`,
    ),

  // ── Suites & parameters ───────────────────────────────────────────────
  suites:      () => request<Suite[]>('/suites'),
  tcParams:    () => request<TCParamsResponse>('/params'),
  saveParams:  (overrides: TCParamsOverrides) =>
    request<{ ok: boolean }>('/params', { method: 'POST', body: JSON.stringify(overrides) }),
  resetParams: () => request<{ ok: boolean }>('/params/reset', { method: 'POST' }),

  // ── YAML test catalogue ───────────────────────────────────────────────
  yamlTests:       () => request<{ tests: YamlTestSummary[]; count: number }>('/yaml-tests'),
  yamlTest:        (id: string) => request<{ id: string; filename: string; content: string }>(`/yaml-tests/${encodeURIComponent(id)}`),
  yamlTestImport:  (content: string, overwrite = false) =>
    request<{ ok: boolean; id: string; filename: string }>('/yaml-tests/import', {
      method: 'POST',
      body: JSON.stringify({ content, overwrite }),
    }),
  yamlTestValidate: (id: string, data: Record<string, unknown>) =>
    request<{ valid: boolean; errors?: SchemaError[] }>(`/yaml-tests/${encodeURIComponent(id)}/validate`, {
      method: 'POST',
      body: JSON.stringify({ data }),
    }),
  yamlTestExport:  (id: string) =>
    request<{ content: string }>(`/yaml-tests/${encodeURIComponent(id)}/export`),

  // ── Schema-driven editor ──────────────────────────────────────────────
  /** JSON Schema (Draft 2020-12) — drives the auto-generated form. */
  testSchema:     () => request<JSONSchema>('/schema/test'),
  /** Metadata for every registered @hook (name, category, inputs). */
  hooksMetadata:  () => request<{ hooks: HookMeta[]; count: number }>('/hooks/metadata'),
  /** Server-side YAML → JSON parse (avoids bundling a YAML lib). */
  yamlTestParse:  (id: string, content: string) =>
    fetch(`${BASE}/yaml-tests/${encodeURIComponent(id)}/parse`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-yaml', ...authHeader() },
      body: content,
    }).then(async (r) => {
      if (!r.ok) {
        let body: unknown = null;
        try { body = await r.json(); } catch { body = await r.text().catch(() => null); }
        throw new ApiError(r.status, body);
      }
      return r.json() as Promise<{ data: Record<string, unknown> }>;
    }),
  /** JSON → canonical YAML, atomic save. 422 on validation failure. */
  yamlTestPatch:  (id: string, data: Record<string, unknown>, validateOnly = false) =>
    request<{ yaml: string; id: string }>(`/yaml-tests/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ data, validate_only: validateOnly }),
    }),

  // ── User suites ───────────────────────────────────────────────────────
  userSuites:       () => request<UserSuite[]>('/user-suites'),
  saveUserSuites:   (suites: UserSuite[]) =>
    request<{ saved: boolean; count: number }>('/user-suites', { method: 'POST', body: JSON.stringify(suites) }),
  deleteUserSuite:  (id: string) =>
    request<{ deleted: boolean; id: string }>(`/user-suites/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  // ── Sentinel ──────────────────────────────────────────────────────────
  sentinelSimulation: () => request<SentinelSnapshot>('/sentinel/simulation'),
  sentinelReal:       () => request<SentinelRealResponse>('/sentinel/real'),
  sentinelAlerts:     (days = 7) => request<SentinelAlertsResponse>(`/sentinel/alerts?days=${days}`),

  // ── Auth (Axe 3 audit S++) ────────────────────────────────────────────
  authStatus: () => request<{
    enabled: boolean;
    token_configured: boolean;
    env_enabled: boolean;
    dev_toggle_allowed: boolean;
    mode: 'dev' | 'prod';
  }>('/auth/status'),
  authToggle: (enabled: boolean) =>
    request<{ enabled: boolean; mode: 'dev' | 'prod' }>('/auth/toggle', {
      method: 'POST',
      body: JSON.stringify({ enabled }),
    }),
};

// ── Local token management (Axe 3) ────────────────────────────────────────
export const authToken = {
  get: (): string => (typeof localStorage !== 'undefined' ? (localStorage.getItem('lab.apitoken') ?? '') : ''),
  set: (token: string): void => {
    if (typeof localStorage === 'undefined') return;
    if (token) localStorage.setItem('lab.apitoken', token);
    else localStorage.removeItem('lab.apitoken');
  },
  clear: (): void => {
    if (typeof localStorage !== 'undefined') localStorage.removeItem('lab.apitoken');
  },
};

export { ApiError };
